import { useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [activeTab, setActiveTab] = useState<'intro' | 'reputation' | 'contact'>('intro');

  const tabs = [
    { key: 'intro' as const, label: '品牌介绍', icon: 'ri-building-2-line' },
    { key: 'reputation' as const, label: '用户口碑', icon: 'ri-heart-line' },
    { key: 'contact' as const, label: '联系我们', icon: 'ri-customer-service-2-line' },
  ];

  const metaTitle = '关于51动漫网 - 品牌介绍 | 51动漫在线观看免费全集';
  const metaDescription = '51动漫网品牌介绍，了解51动漫在线观看平台的故事与愿景。51动漫网致力于打造最优质的动漫免费观看平台，提供日漫韩漫国漫高清全集追更服务。';
  const metaKeywords = '关于51动漫网,51动漫网品牌介绍,51动漫,51动漫在线观看,动漫追更平台';

  return (
    <div className="pt-20 pb-14">
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <link rel="canonical" href="https://www.51dongman.com/about" />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content="https://www.51dongman.com/about" />
      <meta property="og:type" content="website" />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: metaTitle,
            description: metaDescription,
            url: 'https://www.51dongman.com/about',
            isPartOf: { '@type': 'WebSite', name: '51动漫网', url: 'https://www.51dongman.com/' },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.51dongman.com/' },
              { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.51dongman.com/about' },
            ],
          }),
        }}
      />

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

        <div className="bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden mb-10">
          <div className="p-8 md:p-12 text-center border-b border-background-200/50">
            <div className="w-20 h-20 mx-auto mb-5 flex items-center justify-center rounded-2xl bg-primary-50">
              <i className="ri-play-fill text-4xl text-primary-500"></i>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl text-foreground-900 mb-4">关于51动漫网</h1>
            <p className="text-sm text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              51动漫网是新一代的动漫在线观看平台，致力于为全球中文用户提供免费、高清、流畅的动漫追更体验。
            </p>
          </div>

          <div className="flex border-b border-background-200/50 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 min-w-[100px] flex items-center justify-center gap-2 px-6 py-4 text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border-b-2 ${
                  activeTab === tab.key
                    ? 'border-primary-500 text-primary-500 bg-primary-50/50'
                    : 'border-transparent text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
                }`}
              >
                <i className={tab.icon}></i>
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'intro' && (
            <div className="p-8 md:p-12">
              <div className="max-w-3xl">
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-6">我们的故事</h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    <strong>51动漫网</strong>成立于2020年，由一群热爱动漫的年轻创业者共同创建。我们深知动漫爱好者对于高质量内容的需求，因此从创立之初就确立了「免费、高清、全」的核心理念。
                  </p>
                  <p>
                    经过数年发展，51动漫网已成长为覆盖<strong>日漫、韩漫、国漫</strong>三大品类的综合性动漫平台。我们与国内外多家内容提供商建立了合作关系，确保<strong>动漫追更</strong>内容的丰富性和时效性。
                  </p>
                  <p>
                    51动漫网的<strong>下拉式阅读</strong>功能是我们独创的阅读体验——用户只需向下滚动页面即可连续追更，告别繁琐的翻页操作，让追漫体验更加流畅自然。这一创新功能受到了广大<strong>51动漫在线观看</strong>用户的一致好评。
                  </p>

                  <h3 className="font-heading text-lg text-foreground-900 mt-8 mb-4">核心优势</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { icon: 'ri-shield-check-line', title: '完全免费', desc: '51动漫网所有内容均可免费观看，无需付费订阅' },
                      { icon: 'ri-hd-line', title: '高清画质', desc: '支持高清画质播放，提供最佳视觉体验' },
                      { icon: 'ri-speed-up-line', title: '极速加载', desc: '全球CDN加速，确保流畅的动漫追更体验' },
                      { icon: 'ri-smartphone-line', title: '多端适配', desc: '完美适配电脑、平板、手机等各类设备' },
                      { icon: 'ri-refresh-line', title: '每日更新', desc: '同步各大平台更新，51动漫在线观看不滞后' },
                      { icon: 'ri-folders-line', title: '海量资源', desc: '日漫韩漫国漫全覆盖，51动漫全集尽在掌握' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3 bg-background-100 rounded-xl p-4">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-50 flex-shrink-0">
                          <i className={`${item.icon} text-primary-500`}></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-800">{item.title}</p>
                          <p className="text-xs text-foreground-500 mt-1">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'reputation' && (
            <div className="p-8 md:p-12">
              <div className="max-w-3xl">
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-6">用户口碑</h2>
                <p className="text-sm text-foreground-500 mb-8">来自51动漫网用户的真实评价，您的支持是我们前进的动力。</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { name: '动漫达人小王', rating: 5, comment: '51动漫网真的是我看过最好的免费动漫网站！资源超全，日漫韩漫国漫都有，而且更新速度也很快，下拉式阅读体验太棒了，强烈推荐！' },
                    { name: '追漫少女阿琳', rating: 5, comment: '用51动漫网追更已经一年多了，画质清晰流畅，界面也很简洁好看。最爱的就是51动漫全集功能，想回顾以前的剧情超方便！' },
                    { name: '二次元老司机', rating: 5, comment: '对比了很多动漫网站，51动漫网的综合体验是最好的。免费高清无广告骚扰，51动漫在线观看体验一流，已经推荐给身边所有漫友了。' },
                    { name: '漫画爱好者张哥', rating: 5, comment: '51动漫网的下拉式阅读真的太方便了！再也不用一页一页翻，直接往下滑就能连续看，追更效率直接翻倍。日漫韩漫国漫资源也都非常齐全。' },
                    { name: '大学生小李', rating: 4, comment: '51动漫网整体体验非常好，动漫类型覆盖很全。希望51动漫网能继续增加更多新番资源，会一直支持下去的！' },
                    { name: '资深动漫评论人', rating: 5, comment: '作为动漫领域的专业评论人，我认为51动漫网在内容组织和用户体验上都达到了行业领先水平。51动漫免费观看的定位也让它成为大众追漫的首选。' },
                  ].map((review, i) => (
                    <div key={i} className="bg-background-100 rounded-xl p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-primary-600 font-heading text-sm">{review.name[0]}</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-800">{review.name}</p>
                          <div className="flex items-center gap-0.5 mt-0.5">
                            {Array.from({ length: 5 }, (_, j) => (
                              <i
                                key={j}
                                className={`text-[10px] ${j < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                              ></i>
                            ))}
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed">{review.comment}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-8 bg-primary-50 rounded-xl p-6 text-center">
                  <p className="font-heading text-lg text-primary-700 mb-2">平台综合评分</p>
                  <p className="text-4xl font-bold text-primary-500">4.8</p>
                  <p className="text-sm text-primary-400 mt-1">基于超过 50,000 名用户评价</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="p-8 md:p-12">
              <div className="max-w-3xl">
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-6">联系我们</h2>
                <p className="text-sm text-foreground-500 mb-8">
                  如果您在使用51动漫网的过程中遇到任何问题，或者有合作意向，欢迎通过以下方式联系我们。
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                  <div className="bg-background-100 rounded-xl p-6">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-50 mb-4">
                      <i className="ri-mail-line text-xl text-primary-500"></i>
                    </div>
                    <h3 className="font-semibold text-foreground-900 text-sm mb-2">电子邮件</h3>
                    <p className="text-sm text-foreground-500">contact@51dongman.com</p>
                    <p className="text-xs text-foreground-400 mt-1">我们会在24小时内回复</p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-6">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-50 mb-4">
                      <i className="ri-wechat-line text-xl text-primary-500"></i>
                    </div>
                    <h3 className="font-semibold text-foreground-900 text-sm mb-2">微信公众号</h3>
                    <p className="text-sm text-foreground-500">51动漫网</p>
                    <p className="text-xs text-foreground-400 mt-1">关注获取最新追更资讯</p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-6">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-50 mb-4">
                      <i className="ri-bilibili-line text-xl text-primary-500"></i>
                    </div>
                    <h3 className="font-semibold text-foreground-900 text-sm mb-2">B站官方号</h3>
                    <p className="text-sm text-foreground-500">51动漫网官方</p>
                    <p className="text-xs text-foreground-400 mt-1">最新动漫资讯同步更新</p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-6">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-50 mb-4">
                      <i className="ri-question-answer-line text-xl text-primary-500"></i>
                    </div>
                    <h3 className="font-semibold text-foreground-900 text-sm mb-2">商务合作</h3>
                    <p className="text-sm text-foreground-500">business@51dongman.com</p>
                    <p className="text-xs text-foreground-400 mt-1">广告投放、内容合作等</p>
                  </div>
                </div>

                <div className="bg-background-100 rounded-xl p-6 md:p-8">
                  <h3 className="font-semibold text-foreground-900 text-sm mb-5">给我们留言</h3>
                  <form className="space-y-4" data-readdy-form action="https://readdy.ai/api/form/d9oojpaqs38sj6jc7cn0" method="POST">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                        <input
                          type="text"
                          id="contact-name"
                          name="name"
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">主题</label>
                      <input
                        type="text"
                        id="contact-subject"
                        name="subject"
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                        placeholder="请输入留言主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">留言内容</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={5}
                        maxLength={500}
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200 resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden={true}
                      readOnly
                    />
                    <button
                      type="submit"
                      className="px-8 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
                    >
                      发送留言
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}